import {
  Component,
  inject,
  OnDestroy,
  OnInit,
  signal,
  Signal,
  viewChild,
  ViewEncapsulation,
  WritableSignal,
} from '@angular/core';
import {MatDivider} from '@angular/material/divider';
import {TransportDetailComponent} from '../process-overview/cart-details/transport-detail/transport-detail.component';
import {
  ClientsComparatorTableComponent
} from '../process-overview/cart-details/clients-comparator-table/clients-comparator-table.component';
import {MatProgressBar} from '@angular/material/progress-bar';
import {IClientsTileApi} from '../process-overview/cart-details/conspicuity-detail/clients-tile.interface';
import {ColDef, GridApi, ITooltipParams} from 'ag-grid-community';
import {AgGridModule} from 'ag-grid-angular';
import {
  NotBillableFormComponent
} from '../process-overview/cart-details/conspicuity-detail/not-billable-form/not-billable-form.component';
import {concatMap, debounceTime, filter, tap} from 'rxjs/operators';
import {
  changes,
  CheckboxElementComponent,
  ControlStore,
  EsvgFiles,
  IControlDataChanged,
  IControlObject,
  ISelectFormElement,
  ISelectFormOption,
  ResizedDirective,
  SelectElementComponent,
  ToolbarStore,
} from 'frontier';
import {MatDialogRef} from '@angular/material/dialog';
import {
  CartSelectionComponent
} from '../process-overview/cart-details/conspicuity-detail/cart-selection/cart-selection.component';
import {
  ICartControlObj
} from '../process-overview/cart-details/conspicuity-detail/cart-selection/cart-control-obj.interface';
import {combineLatest, Observable, of, startWith, Subscription, Unsubscribable} from 'rxjs';
import {CrewDetailComponent} from '../process-overview/cart-details/crew-detail/crew-detail.component';
import {FormBuilder, FormControl, FormGroup} from '@angular/forms';
import {MatButton} from '@angular/material/button';
import {
  ClientComparatorNotBillableFormTemplateComponent
} from '../client-comparator-not-billable-form-template/client-comparator-not-billable-form-template.component';
import {CartFilterSelectionComponent} from './cart-filter-selection.component';
import {Column} from 'ag-grid-community/dist/lib/entities/column';
import {CellRendererIconComponent} from './cell-renderer-icon/cell-renderer-icon.component';
import AppControlGuids from '../../../api/ControlGuids';
import {ClientDetailComponent} from '../process-overview/cart-details/client-detail/client-detail.component';
import {MatIcon} from '@angular/material/icon';
import {CartListService, TCart, TStation} from '../../../api/generated';
import {CartCreationService} from '../process-overview/carts/cart-creation.service';

interface IMissionDataCollectionForm {
  ambulanceStation: FormControl<TStation>;
  cart: FormControl<TCart>;
  cartsWithConspicuities: FormControl<boolean>;
}

@Component({
  selector: 'app-mission-data-collection',
  standalone: true,
  imports: [
    MatDivider,
    TransportDetailComponent,
    ClientsComparatorTableComponent,
    MatProgressBar,
    AgGridModule,
    NotBillableFormComponent,
    CrewDetailComponent,
    SelectElementComponent,
    CheckboxElementComponent,
    MatButton,
    CartFilterSelectionComponent,
    ClientDetailComponent,
    MatIcon,
    ResizedDirective,
  ],
  templateUrl: './mission-data-collection.component.html',
  styleUrl: './mission-data-collection.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class MissionDataCollectionComponent extends ClientComparatorNotBillableFormTemplateComponent
  implements OnInit, OnDestroy {
  protected readonly EsvgFiles: typeof EsvgFiles = EsvgFiles;
  fb: FormBuilder = inject(FormBuilder);
  private _toolbarStore: ToolbarStore = inject(ToolbarStore);
  private _cartListService: CartListService = inject(CartListService);
  private _cartCreationService: CartCreationService = inject(CartCreationService);
  private subs: Subscription = new Subscription();
  private readonly controlStore: ControlStore = inject(ControlStore);

  cartFilterSelectionRef: Signal<CartFilterSelectionComponent> = viewChild(CartFilterSelectionComponent);
  clientsComparatorRef: Signal<ClientsComparatorTableComponent> = viewChild(ClientsComparatorTableComponent);
  conspicuityMessage: WritableSignal<string> = signal<string>('');

  form: FormGroup<IMissionDataCollectionForm> = this.fb.group({
    ambulanceStation: this.fb.control(null),
    cart: this.fb.control(null),
    cartsWithConspicuities: this.fb.control(true),
  });

  missionDataCollectionGridApi: GridApi;

  override colDefs: ColDef[] = [
    {field: 'label', width: 124, cellStyle: {'font-weight': 'bold'}},
    {
      field: 'value',
      tooltipValueGetter: (params: ITooltipParams): string => {
        // Calculate estimated cell width based on column width
        const columnWidth: number = params?.column?.getActualWidth(); // Get the width of the column
        if (!columnWidth) return '';

        if (params?.data?.label === 'Prüfung' && params.data.value) {
          this.conspicuityMessage.set(params.data.value.replace('Trapo fehlt.', '').trim());
        }

        const threshold: number = (columnWidth - 34) / 12; // Set a threshold relative to the column width
        return params.value?.length > threshold ? params.value : '';
      },
      cellRenderer: CellRendererIconComponent,
    },
  ];

  ambulanceStationData: WritableSignal<ISelectFormElement> = signal<ISelectFormElement>({
    label: 'Rettungswache',
    options: [],
    createNewEnabled: false,
    deleteElementEnabled: false,
    editElementEnabled: false,
    emptyOptionAdded: false,
  });

  constructor() {
    super();
    this._cartListService.cartListGetLocations()
      .subscribe((res): void => {
        console.log(res);
        this.ambulanceStationData.update((state: ISelectFormElement): ISelectFormElement => {
            return {
              ...state, options: res.map((val: TStation) => {
                return {name: val.name, value: val};
              })
            };
          }
        );
      });
  }

  ngOnInit(): void {
    this.subs.add(
      combineLatest([
        this._toolbarStore.toolbarFilterDto$,
        this.form.controls.ambulanceStation.valueChanges.pipe(
          debounceTime(300),
          startWith(''),
        ),
        this.form.controls.cartsWithConspicuities.valueChanges.pipe(startWith(true)),
      ]).pipe(
        tap(([globalFilter, ambulanceStation, cartsWithConspicuities]) => {
          // Update the apiInstance and trigger the fetching of the data with the new filter.
          this.cartFilterSelectionRef().patchFilter({
            ...globalFilter,

            dbobject: ambulanceStation ?? undefined,
            binaryfilter1: cartsWithConspicuities,
          });
        }),
      ).subscribe()
    );

    this.subs.add(
      this.controlStore.controlDataChanged$.pipe(
        filter((evt: IControlDataChanged): boolean => {
          return new Map([
            [AppControlGuids.TTransportList, changes],
            [AppControlGuids.TNotBillable, changes],
            [AppControlGuids.TAllClientList, changes],
            [AppControlGuids.TCartList, changes],
            [AppControlGuids.TCrewList, changes],
            [AppControlGuids.TOrdinanceList, changes],
            [AppControlGuids.TClientList, changes],
          ]).get(evt.GUID)?.has(evt.changeType);
        }),
        tap((): void => {
          this.notBillableFormRef().changeAndFetchInstance();
          this.cartFilterSelectionRef().changeAndFetchInstance();
        }),
      ).subscribe()
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  shrinkClientComparatorColumns(): void {
    const allColumnIds = this.clientsComparatorRef().gridOptions.columnApi.getColumns()
      .map((column: Column) => column.getId());

    allColumnIds.forEach(colId => {
      this.clientsComparatorRef().gridOptions.columnApi.setColumnWidth(colId, 100);
    });
  }

  onTakeDataOfClient(client: IClientsTileApi): void {
    this._notBillableService.notBillableGetCartsOfClient({
      InstanceId: this.notBillableFormRef().instanceId()
    }).pipe(
      concatMap(
        (otherCartsOfClient: IControlObject[]) => {
          const dialogRef: MatDialogRef<CartSelectionComponent> = this._dialogService.dialog.open(
            CartSelectionComponent,
            {
              data: {
                client,
                selectedCart: this.selectedCart()?.obj ?? null,
                carts: otherCartsOfClient,
              },
            }
          )
          return dialogRef.afterClosed();
        }
      ),
      concatMap((carts: ICartControlObj[] | null): Observable<boolean> => {
        if (carts) {
          return this._notBillableService.notBillableReplaceClientForCart(
            {
              InstanceId: this.notBillableFormRef().instanceId(),
              client: client.client,
              SelectedCarts: carts,
            })
        }
        return of(null);
      }),
    ).subscribe();
  }

  separateClientReference(): void {
    this._notBillableService.notBillableSeperateClientReference(
      {InstanceId: this.notBillableFormRef().instanceId()}).subscribe();
  }

  navigateCart(direction: 'next' | 'previous'): void {
    const currentObjId: string = this.form.value.cart?.signature;
    const options: ISelectFormOption[] = this.cartFilterSelectionRef().selectionData.options;
    const currentIndex: number = options.findIndex(option => option.value.signature === currentObjId);
    let newIndex: number;

    if (direction === 'next') {
      newIndex = (currentIndex + 1) % options.length;
    } else {
      newIndex = currentIndex > 0 ? currentIndex - 1 : options.length - 1;
    }

    const newObj: ISelectFormOption = options[newIndex] || null;
    this.form.controls.cart.setValue(newObj?.value);
  }

  createNewCart(): void {
    this._cartCreationService.createMission(null).subscribe((res): void => {
      if (res) {
        this._processOverviewService.selectedCart$.next({
          obj: res.obj,
          number: res.number,
        });
      }
    });
  }
}
